import React from 'react';
import type { IconBaseProps, IconType } from 'react-icons/lib';
import { FaWindows } from 'react-icons/fa6';
import { MdAssignment, MdPaid, MdQueryStats } from 'react-icons/md';
import {
  SiAsterisk,
  SiCloudflare,
  SiCypress,
  SiDebian,
  SiDocker,
  SiGit,
  SiGnubash,
  SiGoogleads,
  SiGoogleanalytics,
  SiGooglesearchconsole,
  SiGrafana,
  SiJavascript,
  SiJenkins,
  SiJira,
  SiMariadb,
  SiMysql,
  SiNodedotjs,
  SiPortainer,
  SiPostman,
  SiProxmox,
  SiStripe,
  SiTrello,
  SiUbuntu,
  SiVmware,
  SiWordpress,
} from 'react-icons/si';
import { VscVscode } from 'react-icons/vsc';

export type TechItem = {
  label: string;
  Icon: IconType;
  color: string;
};

/** Repère visuel proche du logo Playwright (triangles verts) */
export const PlaywrightMark: IconType = ({ size = '1em', title = 'Playwright', ...rest }: IconBaseProps) => (
  <svg
    role="img"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    xmlns="http://www.w3.org/2000/svg"
    {...rest}
  >
    {title ? <title>{title}</title> : null}
    <path fill="#2EAD33" d="M4 20 12 4l3.5 6.5L8.5 20H4z" />
    <path fill="#1B8F24" d="M12 4 20 20h-4.5L9.5 10.5 12 4z" />
  </svg>
);

export const techLanguages: TechItem[] = [
  { label: 'JavaScript', Icon: SiJavascript, color: '#F7DF1E' },
  { label: 'Node.js', Icon: SiNodedotjs, color: '#339933' },
  { label: 'Bash', Icon: SiGnubash, color: '#4EAA25' },
  { label: 'SQL', Icon: SiMysql, color: '#4479A1' },
];

export const techTools: TechItem[] = [
  { label: 'Windows / Windows Server', Icon: FaWindows, color: '#0078D6' },
  { label: 'Debian', Icon: SiDebian, color: '#A81D33' },
  { label: 'Ubuntu', Icon: SiUbuntu, color: '#E95420' },
  { label: 'Git', Icon: SiGit, color: '#F05032' },
  { label: 'VMware', Icon: SiVmware, color: '#607078' },
  { label: 'Docker', Icon: SiDocker, color: '#2496ED' },
  { label: 'Jenkins', Icon: SiJenkins, color: '#D24939' },
  { label: 'Cypress', Icon: SiCypress, color: '#17202C' },
  { label: 'Playwright', Icon: PlaywrightMark, color: '#2EAD33' },
  { label: 'Postman', Icon: SiPostman, color: '#FF6C37' },
  { label: 'Squash', Icon: MdAssignment, color: '#6366f1' },
  { label: 'Jira', Icon: SiJira, color: '#0052CC' },
  { label: 'Trello', Icon: SiTrello, color: '#0052CC' },
  { label: 'VS Code', Icon: VscVscode, color: '#007ACC' },
  { label: 'MariaDB', Icon: SiMariadb, color: '#003545' },
];

export function TechChipList({ items, dense }: { items: TechItem[]; dense?: boolean }) {
  return (
    <ul className={dense ? 'cv-chip-list cv-chip-list-dense cv-chip-list--icons' : 'cv-chip-list cv-chip-list--icons'}>
      {items.map(({ label, Icon, color }) => {
        const Ico = Icon as React.ElementType;
        return (
          <li key={label} className="cv-chip-with-icon">
            <span className="cv-chip-icon-wrap" style={{ color }} aria-hidden>
              <Ico className="cv-chip-svg" title="" />
            </span>
            <span className="cv-chip-label">{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

export const personalEnvItems: TechItem[] = [
  { label: 'Proxmox', Icon: SiProxmox, color: '#E57000' },
  { label: 'Zabbix', Icon: MdQueryStats, color: '#CC0000' },
  { label: 'Grafana', Icon: SiGrafana, color: '#F46800' },
  { label: 'Cloudflare', Icon: SiCloudflare, color: '#F38020' },
  { label: 'Stripe', Icon: SiStripe, color: '#635BFF' },
  { label: 'Google Ads', Icon: SiGoogleads, color: '#4285F4' },
  { label: 'Portainer', Icon: SiPortainer, color: '#13BEF9' },
  { label: 'Asterisk', Icon: SiAsterisk, color: '#F68F1E' },
  { label: 'WordPress', Icon: SiWordpress, color: '#21759B' },
  { label: 'WHMCS', Icon: MdPaid, color: '#32475C' },
  { label: 'Google Analytics', Icon: SiGoogleanalytics, color: '#E37400' },
  { label: 'Search Console', Icon: SiGooglesearchconsole, color: '#458CF5' },
];

